import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { useEnquiryForm } from '../../hooks/useEnquiryForm';
import { courseGroups, courses } from '../../data/courses';
import { destinations } from '../../data/destinations';
import { contact } from '../../data/contact';
interface EnquiryFormProps {
  submitLabel: string;
  defaultCourse?: string;
  defaultLocation?: string;
  showCourseFields?: boolean;
  messageLabel?: string;
}
const fieldBase = 'mt-1.5 block w-full rounded-xl border-2 bg-white px-4 type-field text-ink placeholder:text-muted outline-none transition-colors duration-150 focus:border-brand';
export function EnquiryForm({
  submitLabel,
  defaultCourse = '',
  defaultLocation = '',
  showCourseFields = true,
  messageLabel = 'Your message (optional)'
}: EnquiryFormProps) {
  const {
    values,
    errors,
    status,
    update,
    submit,
    reset
  } = useEnquiryForm({
    course: defaultCourse,
    location: defaultLocation
  });
  const border = (key: keyof typeof errors) => errors[key] ? 'border-danger' : 'border-line';
  return <AnimatePresence mode="wait" initial={false} data-id="element-333" data-mp="11q6pi89w9sgr">
      {status === 'success' ? <motion.div key="success" initial={{
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} role="status" className="flex flex-col items-start" data-id="element-334" data-mp="1fhbiwsmo5oin">
          <CheckCircle2Icon className="h-10 w-10 text-success" aria-hidden="true" data-id="element-335" data-mp="1kgxwj36et6rz" />
          <h3 className="mt-4 type-h3 text-ink" data-id="element-336" data-mp="1ye30mxmkbvkl">
            Thank you, {values.name.split(' ')[0]}
          </h3>
          <p className="mt-3 type-body" data-id="element-337" data-mp="1tdf8g8mll8os">
            Your enquiry is with our admissions team. We will reply to {values.email}
            {values.phone ? ' and send you a WhatsApp message' : ''}.
          </p>
          <button type="button" onClick={reset} className="mt-6 inline-flex h-11 items-center rounded-[15px] border border-ink bg-white px-5 type-button font-bold text-ink transition-colors duration-150 hover:bg-mist" data-id="element-338" data-mp="3v997eg2dlw99">
            Write another enquiry
          </button>
        </motion.div> : <motion.form key="form" noValidate onSubmit={submit} initial={false} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} className="space-y-5" data-id="element-339" data-mp="2d4kisga0cmc3">
          {status === 'error' ? <div role="alert" className="flex gap-3 rounded-xl border-2 border-danger bg-white p-4 type-card-desc text-ink" data-id="element-340" data-mp="2n0xgdp8obkc4">
              <AlertCircleIcon className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" data-id="element-341" data-mp="2rsxinoc7upbv" />
              <p data-id="element-342" data-mp="2wq78v67fj50r">
                We could not send your enquiry. Please check your internet connection and try again, or write to{' '}
                <a href={contact.emailLink} className="font-semibold underline" data-id="element-343" data-mp="30r97bktivd1o">
                  {contact.email}
                </a>
                .
              </p>
            </div> : null}
          <div className="grid gap-5 sm:grid-cols-2" data-id="element-344" data-mp="p5qitxjgpz77">
            <Field label="Full name" error={errors.name} htmlFor="enq-name" data-id="element-345" data-mp="p4yvfa00r7s0">
              <input id="enq-name" name="name" type="text" autoComplete="name" value={values.name} onChange={e => update('name', e.target.value)} className={`${fieldBase} h-12 ${border('name')}`} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'enq-name-error' : undefined} data-id="element-346" data-mp="l3dhdlbr0ng8" />
            </Field>
            <Field label="Email" error={errors.email} htmlFor="enq-email" data-id="element-347" data-mp="18v0xr9bdwg49">
              <input id="enq-email" name="email" type="email" autoComplete="email" value={values.email} onChange={e => update('email', e.target.value)} className={`${fieldBase} h-12 ${border('email')}`} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'enq-email-error' : undefined} data-id="element-348" data-mp="1427uyqzh7hut" />
            </Field>
          </div>
          <Field label="WhatsApp number (optional)" error={errors.phone} htmlFor="enq-phone" data-id="element-349" data-mp="1rralypg02n1z">
            <input id="enq-phone" name="phone" type="tel" autoComplete="tel" placeholder="+00 000 000 0000" value={values.phone} onChange={e => update('phone', e.target.value)} className={`${fieldBase} h-12 ${border('phone')}`} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'enq-phone-error' : undefined} data-id="element-350" data-mp="1bbrmb55p7fcj" />
          </Field>
          {showCourseFields ? <div className="grid gap-5 sm:grid-cols-2" data-id="element-351" data-mp="1v2d4mfn5l206">
              <Field label="Course" htmlFor="enq-course" data-id="element-352" data-mp="1z2f88ucg85i3">
                <select id="enq-course" name="course" value={values.course} onChange={e => update('course', e.target.value)} className={`${fieldBase} h-12 border-line`} data-id="element-353" data-mp="1z1vp5ab2bb2h">
                  <option value="" data-id="element-354" data-mp="1q9bq48quz4h4">Not sure yet</option>
                  {courseGroups.map(group => <optgroup key={group.id} label={group.label} data-id="element-355" data-mp="2dz610krmaiyq">
                      {courses.filter(course => course.group === group.id).map(course => <option key={course.slug} value={course.slug} data-id="element-356" data-mp="2s3n51qhzzpms">
                            {course.name}
                          </option>)}
                    </optgroup>)}
                </select>
              </Field>
              <Field label="Location" htmlFor="enq-location" data-id="element-357" data-mp="2wx7u2t1m599u">
                <select id="enq-location" name="location" value={values.location} onChange={e => update('location', e.target.value)} className={`${fieldBase} h-12 border-line`} data-id="element-358" data-mp="296ltfmtxur9v">
                  <option value="" data-id="element-359" data-mp="257d3kt20rdjl">Not sure yet</option>
                  {destinations.map(destination => <option key={destination.slug} value={destination.slug} data-id="element-360" data-mp="2e5os8pjbs19e">
                      {destination.name}
                    </option>)}
                </select>
              </Field>
            </div> : null}
          <Field label={messageLabel} htmlFor="enq-message" data-id="element-361" data-mp="3pk29vcnhjkgz">
            <textarea id="enq-message" name="message" rows={4} value={values.message} onChange={e => update('message', e.target.value)} className={`${fieldBase} resize-y py-3 border-line`} data-id="element-362" data-mp="1gfeligbc0ge" />
          </Field>
          <div data-id="element-363" data-mp="agc0xdf8cn8a">
            <label className="flex cursor-pointer items-start gap-3 type-card-desc" data-id="element-364" data-mp="ahvh740vyya2">
              <input name="consent" type="checkbox" checked={values.consent} onChange={e => update('consent', e.target.checked)} className="mt-1 h-5 w-5 shrink-0 rounded border-2 border-line accent-brand" aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'enq-consent-error' : undefined} data-id="element-365" data-mp="fg6pvbfg32qp" />
              <span data-id="element-366" data-mp="3snrodcev9bma">I agree that ES World can contact me about my enquiry, as explained in the privacy policy.</span>
            </label>
            {errors.consent ? <p id="enq-consent-error" className="mt-1.5 type-small font-semibold text-danger" data-id="element-367" data-mp="30547k96wkir0">
                {errors.consent}
              </p> : null}
          </div>
          <button type="submit" disabled={status === 'submitting'} className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[15px] py-2.5 text-center leading-tight sm:whitespace-nowrap bg-brand px-6 type-button font-bold text-white transition-colors duration-150 hover:bg-brand-hover active:bg-brand-press disabled:cursor-wait disabled:bg-brand-press sm:w-auto" data-id="element-368" data-mp="2nn0df8gvclf7">
            {status === 'submitting' ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-369" data-mp="1zx64v33l1j0r" />
                Sending
              </> : submitLabel}
          </button>
        </motion.form>}
    </AnimatePresence>;
}
interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}
function Field({
  label,
  htmlFor,
  error,
  children
}: FieldProps) {
  return <div data-id="element-370" data-mp="39vnpmj1qen4c">
      <label htmlFor={htmlFor} className="type-form-label text-ink" data-id="element-371" data-mp="39st56ripx2ax">
        {label}
      </label>
      {children}
      {error ? <p id={`${htmlFor}-error`} className="mt-1.5 type-small font-semibold text-danger" data-id="element-372" data-mp="2zx7ywh6m9fvd">
          {error}
        </p> : null}
    </div>;
}